// The booking steps, and the order a given consumer actually walks them.
//
// Two settings change that order. Phone verification is only present when the
// customer turns it on, and the location step is skipped outright on a
// single-location calendar or a link that names the location. The ticket's
// table numbers personal_info and phone_verification both 4 for the first
// reason, but a funnel cannot read two different steps at one index: with
// verification on, personal_info really is the fifth thing the consumer sees.
//
// step_index therefore describes the sequence THIS consumer was shown, so
// "dropped off at step 3" always means the third screen they saw. Compare
// calendars on step_id, which is stable; use step_index for depth and ordering.
export const STEPS = {
  LOCATION: 'location',
  SERVICES: 'services',
  DATE_TIME: 'date_time',
  PHONE_VERIFICATION: 'phone_verification',
  PERSONAL_INFO: 'personal_info',
  REVIEW: 'review',
  SUCCESS: 'success',
};

// How the consumer got to a step. `deep_link` and `resume` exist so a step
// nobody was shown is never counted as a drop-off: a Reserve with Google link
// opens straight on date_time, and a returning visitor picks up where they
// left off. `auto_skip` separates a move the WIDGET made — a pinned location,
// a `?service=` link resolving its location — from one the consumer clicked,
// so a screen that only flashed past is not read as a step they worked through.
export const DIRECTIONS = {
  FORWARD: 'forward',
  BACK: 'back',
  RESUME: 'resume',
  DEEP_LINK: 'deep_link',
  AUTO_SKIP: 'auto_skip',
};

const FULL_ORDER = [
  STEPS.LOCATION,
  STEPS.SERVICES,
  STEPS.DATE_TIME,
  STEPS.PHONE_VERIFICATION,
  STEPS.PERSONAL_INFO,
  STEPS.REVIEW,
  STEPS.SUCCESS,
];

export const stepOrder = ({ phoneVerificationRequired, locationStepSkipped } = {}) =>
  FULL_ORDER.filter((step) => {
    if (step === STEPS.PHONE_VERIFICATION) return !!phoneVerificationRequired;
    if (step === STEPS.LOCATION) return !locationStepSkipped;
    return true;
  });

// 1-based, matching the ticket's vocabulary. Returns null for a step this
// calendar never shows, so the caller sends no index rather than a wrong one.
export const stepIndex = (stepId, options) => {
  const position = stepOrder(options).indexOf(stepId);
  return position === -1 ? null : position + 1;
};

export const firstStepId = (options) => stepOrder(options)[0];

// The widget's own `currentStep` runs 1..6 and puts phone verification and the
// personal-info form both on 4, swapping between them on the fly. Analytics
// names them separately, so translate here rather than teaching every caller
// the screen numbering.
export const stepIdForScreen = ({ currentStep, validatePhone, phoneVerified }) => {
  switch (currentStep) {
    case 1:
      return STEPS.LOCATION;
    case 2:
      return STEPS.SERVICES;
    case 3:
      return STEPS.DATE_TIME;
    case 4:
      return validatePhone && !phoneVerified ? STEPS.PHONE_VERIFICATION : STEPS.PERSONAL_INFO;
    case 5:
      return STEPS.REVIEW;
    case 6:
      return STEPS.SUCCESS;
    default:
      return null;
  }
};
